import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, X, Check } from 'lucide-react'
import { listNotifications, markRead, markAllRead, type Notification } from '@/api/notifications'
import { reviewApproval } from '@/api/attendanceApprovals'

export default function NotificationBell() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState<Notification | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const { data } = useQuery({ queryKey: ['notifications'], queryFn: listNotifications, refetchInterval: 30_000, refetchOnWindowFocus: true })
  const items = data?.items ?? []
  const unread = data?.unread ?? 0
  const invalidate = () => qc.invalidateQueries({ queryKey: ['notifications'] })

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const onItem = async (n: Notification) => {
    if (!n.is_read) { await markRead(n.id); invalidate() }
    setOpen(false)
    setActive(n) // open the notification in a popup instead of navigating away
  }

  const goToLink = () => {
    if (active?.link) navigate(`/${orgSlug}${active.link}`)
    setActive(null)
  }

  const [reviewErr, setReviewErr] = useState('')
  const reviewMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'approved' | 'rejected' }) => reviewApproval(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['attendance-approvals'] })
      setActive(null)
      setReviewErr('')
    },
    onError: (e: any) => setReviewErr(e?.response?.data?.detail || 'Could not update the request'),
  })
  // Approver's actionable notification carries a ref_id; result notifications don't.
  const canReview = active?.type === 'attendance_approval' && !!active?.ref_id

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} className="relative p-1.5 text-white/90 hover:text-white">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-white text-[#EC5B2C] text-[10px] font-bold flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[90vw] bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            {unread > 0 && <button onClick={async () => { await markAllRead(); invalidate() }} className="text-xs text-[#EC5B2C] hover:underline">Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">No notifications.</p>
            ) : items.map((n) => (
              <button key={n.id} onClick={() => onItem(n)} className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 ${!n.is_read ? 'bg-[#EC5B2C]/5' : ''}`}>
                <div className="flex items-start gap-2">
                  {!n.is_read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#EC5B2C] shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900">{n.title}</p>
                    {n.body && <p className="text-xs text-gray-500 truncate">{n.body}</p>}
                    <p className="text-[10px] text-gray-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
              <h3 className="text-base font-semibold text-gray-900 pr-2">{active.title}</h3>
              <button onClick={() => setActive(null)} className="text-gray-400 hover:text-gray-600 shrink-0"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-5 py-4">
              {active.body && <p className="text-sm text-gray-700 whitespace-pre-wrap">{active.body}</p>}
              {reviewErr && <p className="mt-2 text-xs text-red-600">{reviewErr}</p>}
              <p className="text-xs text-gray-400 mt-3">{new Date(active.created_at).toLocaleString()}</p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-xl">
              {canReview && (
                <>
                  <button
                    disabled={reviewMut.isPending}
                    onClick={() => reviewMut.mutate({ id: active.ref_id as string, status: 'approved' })}
                    className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  ><Check className="h-4 w-4" /> Approve</button>
                  <button
                    disabled={reviewMut.isPending}
                    onClick={() => reviewMut.mutate({ id: active.ref_id as string, status: 'rejected' })}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >Reject</button>
                </>
              )}
              {!canReview && active.link && <button onClick={goToLink} className="rounded-lg bg-[#EC5B2C] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#d54e22]">Open</button>}
              <button onClick={() => setActive(null)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
